// Local state hook for the administrative-area layer toggle.
// Mirrors use-transport-layer-state.ts's shape for consistency.
// Toggle state is React local state ONLY — NOT in URL, NOT in shared filters, NOT in localStorage.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { administrativeAreaQueries } from '../api/queries';
import type { WithCallWithAuth } from '../api/queries';
import type { AdminBoundaryFeatureCollection } from '@ooh/contracts';
import { useDebouncedBbox, parseBbox, ZERO_BBOX } from '../../../hooks/use-debounced-bbox';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseAdministrativeAreaLayerStateOptions {
  auth: WithCallWithAuth;
  bbox: string | null;
}

export interface UseAdministrativeAreaLayerStateResult {
  /** Whether the administrative-area layer toggle is on. Local UI state only. */
  isEnabled: boolean;
  /** Toggle the administrative-area layer on/off. */
  toggle: () => void;
  /** Administrative-area GeoJSON features for the current debounced viewport. */
  areas: AdminBoundaryFeatureCollection | undefined;
  /** Whether administrative-area geometry is loading. */
  isLoading: boolean;
  /** Whether the administrative-area geometry request failed — a recoverable, isolated
   * error state that never affects `mapPins`/shared filters (see spec scenario
   * "Geometry failure does not break map filtering"). */
  isError: boolean;
}

// ─── Main hook ────────────────────────────────────────────────────────────────

export function useAdministrativeAreaLayerState({
  auth,
  bbox,
}: UseAdministrativeAreaLayerStateOptions): UseAdministrativeAreaLayerStateResult {
  // Local state — NOT in URL, NOT in shared filters
  const [isEnabled, setIsEnabled] = React.useState(false);
  const toggle = React.useCallback(() => setIsEnabled((prev) => !prev), []);

  const debouncedBbox = useDebouncedBbox(bbox, 300);
  const parsedBbox = debouncedBbox !== null ? parseBbox(debouncedBbox) : null;
  const coords = parsedBbox ?? ZERO_BBOX;
  const enabled = isEnabled && parsedBbox !== null;

  const { data, isLoading, isError } = useQuery({
    ...administrativeAreaQueries.listByBbox(auth, coords),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });

  return {
    isEnabled,
    toggle,
    areas: data as AdminBoundaryFeatureCollection | undefined,
    isLoading,
    isError,
  };
}
